import { useEffect, useRef, useState } from "react";
import { Table2, Plus, Search, Filter, ChevronUp, ChevronDown, RefreshCw } from "lucide-react";

import { TranslatedText } from "@/components/common/text";
import { TruckCard } from "@/components/dataPages/loadFinder/truck/TruckCard";
import { AddTruckModal } from "@/components/dataPages/loadFinder/truck/AddTruckModal";
import { EditTruckModal } from "@/components/dataPages/loadFinder/truck/EditTruckModal";
import { DeleteTruckConfirmModal } from "@/components/dataPages/loadFinder/truck/DeleteTruckConfirmModal";
import { truckStatuses, TruckStatus } from "@/utils/constants";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";

// Left pane of the Load Finder: truck search, availability/filters filters, and the truck list.
// Owns its own search/filter/expand UI state and the delete-truck confirm flow; the parent
// provides the trucks data, the current selection, the filter/unread lookups, and the
// selection/add/edit callbacks.
export const TrucksSidebar = ({
    trucks,
    companyTrucks = { data: null },
    selectedTruckId,
    trucksWithFilters,
    trucksWithUnreadLoads,
    onSelectTruck,
    onRunCheck,
}) => {
    const isDarkTheme = useIsDarkTheme();
    const [searchQuery, setSearchQuery] = useState("");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isTrucksExpanded, setIsTrucksExpanded] = useState(true);
    const [isOtherTrucksExpanded, setIsOtherTrucksExpanded] = useState(false);
    const [availabilityFilter, setAvailabilityFilter] = useState(null);
    const [filterPresence, setFilterPresence] = useState(null);
    const [isAddTruckOpen, setIsAddTruckOpen] = useState(false);
    const [truckToEdit, setTruckToEdit] = useState(null);
    const [truckToDelete, setTruckToDelete] = useState(null);

    // The sidebar is only "empty" when there are no personal trucks AND no company trucks —
    // company trucks alone are enough to show the normal list UI.
    const myTrucksEmpty = trucks.data !== null && trucks.data.length === 0;
    const companyTrucksEmpty = !companyTrucks?.data || companyTrucks.data.length === 0;
    const isEmpty = myTrucksEmpty && companyTrucksEmpty;

    const toggleFilter = (current, value, setter) => {
        setter(current === value ? null : value);
    };

    // Shared search + availability matcher, applied to both lists (the filters filter is
    // caller-scoped, so it only applies to "My trucks"). Search matches truck number,
    // driver name, and location.
    const matchesSearchAndAvailability = (truck) => {
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            const truckName = (truck.truckName ?? "").toLowerCase();
            const driverName = (truck.driverName ?? "").toLowerCase();
            const location = truck.location ? `${truck.location.city}, ${truck.location.state}`.toLowerCase() : "";
            if (!truckName.includes(q) && !driverName.includes(q) && !location.includes(q)) return false;
        }
        if (availabilityFilter && (truck.status ?? TruckStatus.FREE.id) !== availabilityFilter) return false;
        return true;
    };

    const filteredTrucks = trucks.data?.filter((truck) => {
        if (!matchesSearchAndAvailability(truck)) return false;
        if (filterPresence !== null) {
            const hasFilter = trucksWithFilters.has(truck.truckId);
            if (filterPresence === "HAS_FILTER" && !hasFilter) return false;
            if (filterPresence === "NO_FILTER" && hasFilter) return false;
        }
        return true;
    });

    // A truck the caller has pinned lives in "My trucks", so it is excluded from "Company trucks".
    const filteredCompanyTrucks = companyTrucks?.data?.filter((truck) => !truck.isMyTruck && matchesSearchAndAvailability(truck));

    // With no pinned trucks, "Company trucks" is the only list there is (and the parent defaults the
    // selection into it) — open it once so the selected truck isn't hidden behind a collapsed header.
    const otherTrucksAutoExpandedRef = useRef(false);
    useEffect(() => {
        if (otherTrucksAutoExpandedRef.current || trucks.data === null) return;
        if (trucks.data.length > 0 || !companyTrucks?.data?.length) return;
        otherTrucksAutoExpandedRef.current = true;
        setIsOtherTrucksExpanded(true);
    }, [trucks.data, companyTrucks?.data]);

    const colorOne = isDarkTheme ? "#8EE4CA" : "#2E5D4B";
    const colorTwo = isDarkTheme ? "#14201E" : "#E9F8F3";

    return (
        <div className="w-[320px] 3xl:w-[340px] shrink-0 border-r border-cm-p-outline px-4 pt-3 pb-[76px] flex flex-col min-h-0 overflow-hidden bg-cm-p-surface">
            <div className="flex items-center gap-3 mb-3 shrink-0">
                <button className="w-7 h-7 rounded-lg bg-cm-p-container-green text-cm-p-on-container-green flex items-center justify-center shrink-0">
                    <Table2 className="w-4 h-4" />
                </button>
                <span className="text-cm-p-text-primary font-medium text-[20px]">
                    <TranslatedText>Trucks</TranslatedText>
                </span>
                {!isEmpty && (
                    <button
                        type="button"
                        onClick={onRunCheck}
                        className="ml-auto flex items-center gap-1.5 text-[14px] font-medium text-cm-p-text-secondary hover:text-cm-p-text-primary"
                    >
                        <RefreshCw className="w-4 h-4" />
                        <TranslatedText>Run check</TranslatedText>
                    </button>
                )}
            </div>

            {!isEmpty && (
                <div className="flex flex-col flex-1 min-h-0">
                    <div className="flex items-center gap-2 mb-3 shrink-0">
                        <div className="flex-1 flex items-center gap-1 bg-cm-p-surface-container border border-cm-p-outline rounded-[6px] px-3 h-10">
                            <Search className="w-4 h-4 text-cm-p-text-secondary shrink-0" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Find your truck"
                                className="flex-1 bg-transparent text-[15px] text-cm-p-text-primary placeholder:text-cm-p-text-secondary placeholder:text-[15px] border-0 outline-none focus:ring-0 focus:shadow-none focus:outline-none px-0"
                            />
                        </div>
                        <button
                            className="w-10 h-10 shrink-0 flex items-center justify-center rounded-[6px] bg-cm-p-surface-container border border-cm-p-outline text-cm-p-text-primary"
                            onClick={() => setIsFilterOpen((v) => !v)}
                        >
                            {isFilterOpen ? <ChevronUp className="w-4 h-4" /> : <Filter className="w-4 h-4" />}
                        </button>
                    </div>

                    <div
                        className={`shrink-0 overflow-hidden transition-all duration-300 ${isFilterOpen ? "max-h-[300px] mb-3" : "max-h-0"}`}
                    >
                        <div className="flex flex-col gap-3">
                            {/* Availability filter */}
                            <div>
                                <p className="text-[12px] font-medium text-cm-p-text-secondary uppercase px-1 mb-1">Availability</p>
                                <div className="flex flex-wrap gap-1">
                                    {truckStatuses.map((status) => (
                                        <button
                                            key={status.id}
                                            className={`px-3 py-1 rounded-lg text-[14px] font-medium border ${
                                                availabilityFilter === status.id
                                                    ? "bg-cm-p-accent-50 border-cm-p-accent-200 text-cm-p-on-container-green"
                                                    : "bg-cm-p-surface-container border-transparent text-cm-p-text-primary"
                                            }`}
                                            onClick={() => toggleFilter(availabilityFilter, status.id, setAvailabilityFilter)}
                                        >
                                            {status.text}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Filters filter */}
                            <div>
                                <p className="text-[12px] font-medium text-cm-p-text-secondary uppercase px-1 mb-1">Filters</p>
                                <div className="flex flex-wrap gap-1">
                                    <button
                                        className={`px-3 py-1 rounded-lg text-[14px] font-medium border ${
                                            filterPresence === "NO_FILTER"
                                                ? "bg-cm-p-accent-50 border-cm-p-accent-200 text-cm-p-on-container-green"
                                                : "bg-cm-p-surface-container border-transparent text-cm-p-text-primary"
                                        }`}
                                        onClick={() => toggleFilter(filterPresence, "NO_FILTER", setFilterPresence)}
                                    >
                                        No filters
                                    </button>
                                    <button
                                        className={`px-3 py-1 rounded-lg text-[14px] font-medium border ${
                                            filterPresence === "HAS_FILTER"
                                                ? "bg-cm-p-accent-50 border-cm-p-accent-200 text-cm-p-on-container-green"
                                                : "bg-cm-p-surface-container border-transparent text-cm-p-text-primary"
                                        }`}
                                        onClick={() => toggleFilter(filterPresence, "HAS_FILTER", setFilterPresence)}
                                    >
                                        Has filters
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-4 border-t border-cm-p-outline shrink-0" />

                    {/* Truck lists share the remaining height: each grows to its content but no further
                        than 50% when both are full (max-content cap), so a short list yields space to a
                        long one, and both scroll when large. The Add button rides at the end of this
                        region — right below the lists when short, pinned to the bottom when full — and
                        the sidebar's bottom padding keeps it clear of the floating "Leave feedback". */}
                    <div className="flex-1 min-h-0 flex flex-col">
                        <button className="flex items-center mb-3 shrink-0" onClick={() => setIsTrucksExpanded((v) => !v)}>
                            <span className="text-[16px] font-medium text-cm-p-text-primary">My trucks</span>
                            <span
                                className="flex items-center justify-center text-[15px] font-semibold shrink-0 ml-[6px]"
                                style={{
                                    color: colorOne,
                                    backgroundColor: colorTwo,
                                    borderRadius: "28px",
                                    width: "28px",
                                    height: "20px",
                                }}
                            >
                                {filteredTrucks?.length ?? 0}
                            </span>
                            <span className="ml-auto text-cm-p-text-secondary">
                                {isTrucksExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </span>
                        </button>

                        <div className={isTrucksExpanded ? "flex-1 basis-0 min-h-0 overflow-y-auto max-h-[max-content]" : "hidden"}>
                            <div className="flex flex-col gap-2 pb-1">
                                {filteredTrucks?.map((truck) => (
                                    <TruckCard
                                        key={truck.truckId}
                                        truck={truck}
                                        selected={selectedTruckId === truck.truckId}
                                        hasFilter={trucksWithFilters.has(truck.truckId)}
                                        hasNewLoads={trucksWithUnreadLoads.has(truck.truckId)}
                                        onClick={() => onSelectTruck(truck.truckId)}
                                        onEdit={setTruckToEdit}
                                        onDelete={setTruckToDelete}
                                    />
                                ))}
                            </div>
                        </div>

                        <button
                            className="shrink-0 mt-3 flex w-full items-center justify-center gap-2 h-[38px] rounded text-[14px] font-medium text-cm-p-text-primary bg-cm-p-surface-container opacity-80 hover:opacity-100"
                            onClick={() => setIsAddTruckOpen(true)}
                        >
                            <Plus className="w-4 h-4" />
                            <TranslatedText>Add truck</TranslatedText>
                        </button>

                        <button className="flex items-center mb-3 mt-6 shrink-0" onClick={() => setIsOtherTrucksExpanded((v) => !v)}>
                            <span className="text-[16px] font-medium text-cm-p-text-primary">Company trucks</span>
                            <span
                                className="flex items-center justify-center text-[15px] font-semibold shrink-0 ml-[6px]"
                                style={{
                                    color: colorOne,
                                    backgroundColor: colorTwo,
                                    borderRadius: "28px",
                                    width: "28px",
                                    height: "20px",
                                }}
                            >
                                {filteredCompanyTrucks?.length ?? 0}
                            </span>
                            <span className="ml-auto text-cm-p-text-secondary">
                                {isOtherTrucksExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </span>
                        </button>

                        <div className={isOtherTrucksExpanded ? "flex-1 basis-0 min-h-0 overflow-y-auto max-h-[max-content]" : "hidden"}>
                            <div className="flex flex-col gap-2 pb-1">
                                {filteredCompanyTrucks?.map((truck) => (
                                    <TruckCard
                                        key={truck.truckId}
                                        truck={truck}
                                        selected={selectedTruckId === truck.truckId}
                                        hoverHighlight={false}
                                        hasFilter={trucksWithFilters.has(truck.truckId)}
                                        hasNewLoads={false}
                                        onClick={() => onSelectTruck(truck.truckId)}
                                        onEdit={setTruckToEdit}
                                        onDelete={setTruckToDelete}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isEmpty && (
                <div className="flex-1 flex flex-col">
                    <p className="text-cm-p-text-secondary text-sm mb-3">
                        <TranslatedText>Looks like you don't have any trucks set up or assigned to you yet!</TranslatedText>
                    </p>
                    <p className="text-cm-p-text-secondary text-sm mb-6">
                        <TranslatedText>
                            Once you add a truck, it will show up here and you will be able to find relevant loads or to simply filter it.
                        </TranslatedText>
                    </p>
                    <button
                        className="w-full flex items-center justify-center gap-1 py-[7px] px-4 rounded-lg bg-cm-p-accent-100 border border-cm-p-accent-300 text-cm-p-on-container-green text-sm font-medium"
                        onClick={() => setIsAddTruckOpen(true)}
                    >
                        <Plus className="w-4 h-4" />
                        <TranslatedText>Add your first truck</TranslatedText>
                    </button>
                </div>
            )}

            <AddTruckModal isOpen={isAddTruckOpen} onClose={() => setIsAddTruckOpen(false)} onSuccess={onSelectTruck} />
            <EditTruckModal isOpen={!!truckToEdit} onClose={() => setTruckToEdit(null)} truck={truckToEdit} />
            <DeleteTruckConfirmModal
                isOpen={!!truckToDelete}
                truck={truckToDelete}
                hasFilter={truckToDelete ? trucksWithFilters.has(truckToDelete.truckId) : false}
                onClose={() => setTruckToDelete(null)}
                onDeleted={(truckId) => {
                    // Deselect in the parent if the truck that was deleted is the selected one.
                    if (selectedTruckId === truckId) onSelectTruck(null);
                }}
            />
        </div>
    );
};
